import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Premium color palette
const COLORS = {
  primary: '#1d4ed8',
  primaryDark: '#1e40af',
  dark: '#1E293B',
  slate: '#64748B',
  light: '#F8FAFC'
};

/**
 * Export Service - BlueGov
 * Serviço de exportação para PDF e Excel
 */

// ============ PDF EXPORT ============

export const exportToPDF = (data, options = {}) => {
  const {
    title = 'Relatório BlueGov',
    subtitle = '',
    filename = 'relatorio-bluegov',
    columns = [],
    tenant = '',
    generatedBy = '',
    orientation = 'portrait'
  } = options;

  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Clean header - solid blue gradient effect
  doc.setFillColor(37, 99, 235); // Blue 600
  doc.rect(0, 0, pageWidth, 35, 'F');

  // Logo/Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('BlueGov', margin, 16);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Sistema de Gestão de Atos de Controle', margin, 24);

  // Tenant badge
  if (tenant) {
    doc.setFontSize(9);
    doc.text(tenant, pageWidth - margin - doc.getTextWidth(tenant), 16);
  }

  // Report title section
  let yPos = 50;
  doc.setTextColor(30, 41, 59); // Dark slate
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, yPos);

  if (subtitle) {
    yPos += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139); // Slate
    doc.text(subtitle, margin, yPos);
  }

  // Metadata line
  yPos += 12;
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  const dateStr = new Date().toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: 'long', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  doc.text(`Gerado em: ${dateStr}${generatedBy ? ` | Por: ${generatedBy}` : ''}`, margin, yPos);

  // Divider line
  yPos += 5;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);

  // Table
  yPos += 10;
  
  if (data.length > 0 && columns.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [columns.map(col => col.header)],
      body: data.map(row => columns.map(col => {
        const value = row[col.key];
        if (col.format) return col.format(value);
        return value ?? '-';
      })),
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: [30, 41, 59],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      bodyStyles: {
        textColor: [71, 85, 105],
      },
      columnStyles: columns.reduce((acc, col, i) => {
        if (col.width) acc[i] = { cellWidth: col.width };
        if (col.align) acc[i] = { ...acc[i], halign: col.align };
        return acc;
      }, {}),
      didDrawPage: (tableData) => {
        // Footer on each page
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Página ${tableData.pageNumber} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          'BlueGov - Documento Oficial',
          margin,
          pageHeight - 10
        );
      }
    });
  }

  // Save
  doc.save(`${filename}-${new Date().toISOString().split('T')[0]}.pdf`);
  return true;
};

// ============ EXCEL EXPORT ============

export const exportToExcel = (data, options = {}) => {
  const {
    title = 'Relatório BlueGov',
    filename = 'relatorio-bluegov',
    columns = [],
    sheetName = 'Dados',
    tenant = ''
  } = options;

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Prepare header row with styled names
  const headers = columns.map(col => col.header);
  
  // Prepare data rows
  const rows = data.map(row => 
    columns.map(col => {
      const value = row[col.key];
      if (col.format) return col.format(value);
      return value ?? '';
    })
  );

  // Create worksheet with headers + data
  const wsData = [
    // Title row
    [title],
    // Subtitle with date and tenant
    [`Gerado em: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}${tenant ? ` | Tenant: ${tenant}` : ''}`],
    // Empty row
    [],
    // Headers
    headers,
    // Data
    ...rows
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const colWidths = columns.map(col => ({ wch: col.width ? col.width / 3 : 15 }));
  ws['!cols'] = colWidths;

  // Merge title cell
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: columns.length - 1 } }
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Generate buffer and save
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}-${new Date().toISOString().split('T')[0]}.xlsx`);
  
  return true;
};

// ============ CSV EXPORT ============

export const exportToCSV = (data, options = {}) => {
  const {
    filename = 'relatorio-bluegov',
    columns = []
  } = options;

  // Headers
  const headers = columns.map(col => col.header).join(';');
  
  // Rows
  const rows = data.map(row => 
    columns.map(col => {
      let value = row[col.key];
      if (col.format) value = col.format(value);
      // Escape quotes and wrap in quotes if contains separator
      if (typeof value === 'string' && (value.includes(';') || value.includes('"'))) {
        value = `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    }).join(';')
  );

  const csv = [headers, ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }); // BOM for Excel
  saveAs(blob, `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
  
  return true;
};

// ============ PRESET COLUMN DEFINITIONS ============

export const COLUMN_PRESETS = {
  acts: [
    { key: 'control_body', header: 'Órgão Emissor', width: 50 },
    { key: 'type', header: 'Tipo', width: 35, format: (v) => v?.replace(/_/g, ' ') },
    { key: 'status', header: 'Status', width: 30 },
    { key: 'severity', header: 'Severidade', width: 25 },
    { key: 'deadline', header: 'Prazo', width: 30, format: (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
    { key: 'description', header: 'Descrição', width: 80 }
  ],
  
  auditLogs: [
    { key: 'timestamp', header: 'Data/Hora', width: 40, format: (v) => v ? new Date(v).toLocaleString('pt-BR') : '-' },
    { key: 'user_email', header: 'Usuário', width: 45 },
    { key: 'action', header: 'Ação', width: 25 },
    { key: 'entity', header: 'Entidade', width: 30 },
    { key: 'details', header: 'Detalhes', width: 60 }
  ],
  
  alerts: [
    { key: 'created_at', header: 'Data', width: 35, format: (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
    { key: 'alert_type', header: 'Tipo', width: 30 },
    { key: 'severity', header: 'Severidade', width: 25 },
    { key: 'message', header: 'Mensagem', width: 70 },
    { key: 'is_resolved', header: 'Resolvido', width: 20, format: (v) => v ? 'Sim' : 'Não' }
  ],

  dashboardSummary: [
    { key: 'metric', header: 'Métrica', width: 50 },
    { key: 'value', header: 'Valor', width: 30, align: 'right' },
    { key: 'trend', header: 'Tendência', width: 25 },
    { key: 'status', header: 'Status', width: 25 }
  ]
};

export default {
  exportToPDF,
  exportToExcel,
  exportToCSV,
  COLUMN_PRESETS
};
